import { DATA_POINTS, TYPES, SHIPPING_UNIT_CODES } from './constants';

export function buildCountryDestination(countryCode = DATA_POINTS.shipping.country.US) {
  return {
    '@type':           TYPES.defined_region,
    'addressCountry':  countryCode,
  }
}

export function buildShippingOrigin() {
  return {
    '@type': TYPES.defined_region,
    'addressCountry': DATA_POINTS.address.addressCountry,
    'addressRegion': DATA_POINTS.address.addressRegion,
    'postalCode': DATA_POINTS.address.postalCode,
  }
}

export function buildBusinessDays(days = []) {
  const dayList = days.length > 0 ? days : DATA_POINTS.shipping.businessDays;
  const validDays = dayList.map(day => 
    day.startsWith('http') ? day : `https://schema.org/${day}`
  );
  return {
    '@type':     TYPES.opening_hours_specification,
    'dayOfWeek':  validDays,
  }
}

export function buildShippingRate(rate = {}) {
  return {
    '@type':    TYPES.monetary_amount,
    'value':     rate.value,
    'currency':  rate.currency || DATA_POINTS.shipping.currency,
  }
}

export function buildDeliveryTime(timing = {}) {
  const defaults = DATA_POINTS.shipping.defaults.freeStandard;

  const formattedCutoffTime = typeof timing.cutoffTime === 'number' 
    ? `${String(timing.cutoffTime).padStart(2, '0')}:00:00Z` 
    : timing.cutoffTime;

  return {
    '@type': TYPES.shipping_delivery_time,
    'handlingTime': {
      '@type':    TYPES.quantitativeValue,
      'minValue':  timing.handlingMin ?? defaults.handlingMin,
      'maxValue':  timing.handlingMax ?? defaults.handlingMax,
      'unitCode':  timing.unitCode ?? SHIPPING_UNIT_CODES.DAY,
      'unitText':  timing.unitText ?? 'days',
    },
    'transitTime': {
      '@type':    TYPES.quantitativeValue,
      'minValue':  timing.transitMin ?? defaults.transitMin,
      'maxValue':  timing.transitMax ?? defaults.transitMax,
      'unitCode':  timing.unitCode ?? SHIPPING_UNIT_CODES.DAY,
      'unitText':  timing.unitText ?? 'days',
    },
    'cutoffTime': formattedCutoffTime || DATA_POINTS.shipping.cutoffTime,
    'businessDays': buildBusinessDays(timing.businessDays || []),
  }
}

export function buildShippingDetails(shipping, options = {}) {
  if (!shipping) return undefined

  return {
    '@type': TYPES.offer_shipping_details,

    ...(shipping.doesNotShip ? {} : {
      'shippingRate': buildShippingRate({
        value:    shipping.cost ?? 0,
        currency: shipping.currency || options.currency
      }),
    }),

    'doesNotShip': shipping.doesNotShip === true ? true : undefined,

    'shippingDestination': shipping.destination
      || buildCountryDestination(shipping.country),

    'shippingOrigin': buildShippingOrigin(),

    'shippingLabel': shipping.label,

    ...(shipping.doesNotShip ? {} : {
      'deliveryTime': buildDeliveryTime({
          handlingMin:  shipping.handlingMin,
          handlingMax:  shipping.handlingMax,
          transitMin:   shipping.transitMin,
          transitMax:   shipping.transitMax,
          cutoffTime:   shipping.cutoffTime,
          businessDays: shipping.businessDays
        })
    }),
  }
}


/**
 * Factory function to build specific shipping options without repeating boilerplate logic.
 * Merges the passed options with the defaults defined in DATA_POINTS.shipping.defaults.
 */
function buildSpecificShipping(type, defaultLabel, options = {}) {
  const defaults = DATA_POINTS.shipping.defaults[type];
  return buildShippingDetails({
    doesNotShip:   options.doesNotShip ?? options.does_not_ship,
    cost:          options.cost ?? options.shipping_cost ?? defaults.cost ?? 0,
    currency:      options.currency,
    country:       options.country ?? options.country_code,
    destination:   options.destination,
    label:         options.label ?? options.shippingLabel ?? defaultLabel,
    handlingMin:   options.handlingMin ?? options.handling_min ?? defaults.handlingMin,
    handlingMax:   options.handlingMax ?? options.handling_max ?? defaults.handlingMax,
    transitMin:    options.transitMin  ?? options.transit_min  ?? defaults.transitMin,
    transitMax:    options.transitMax  ?? options.transit_max  ?? defaults.transitMax,
    cutoffTime:    options.cutoffTime  ?? options.cutoff_time,
    businessDays:  options.businessDays ?? options.business_days,
  }, options);
}

const SHIPPING_BUILDERS = {
  freeStandard: (options) => buildSpecificShipping('freeStandard', DATA_POINTS.shipping.defaults.freeStandard.label, options),
  paidStandard: (options) => buildSpecificShipping('paidStandard', DATA_POINTS.shipping.defaults.paidStandard.label, options),
  express:      (options) => buildSpecificShipping('express', DATA_POINTS.shipping.defaults.express.label, options),
  sameDay:      (options) => buildSpecificShipping('sameDay', DATA_POINTS.shipping.defaults.sameDay.label, options),
  international:(options) => buildSpecificShipping('international', DATA_POINTS.shipping.defaults.international.label, options),
};

export const buildShippingByType = (type, options = {}) => {  
  const builder = SHIPPING_BUILDERS[type];
  if (!builder) {
    console.warn(`[OfferShippingDetailSchema] Unknown shipping type: "${type}".`);
    return null;
  }
  return builder(options);
};

export const buildFreeStandardShipping  = (options = {}) => SHIPPING_BUILDERS.freeStandard(options);
export const buildPaidStandardShipping  = (options = {}) => SHIPPING_BUILDERS.paidStandard(options);
export const buildExpressShipping       = (options = {}) => SHIPPING_BUILDERS.express(options);
export const buildSameDayShipping       = (options = {}) => SHIPPING_BUILDERS.sameDay(options);
export const buildInternationalShipping = (options = {}) => SHIPPING_BUILDERS.international(options);


